import type { PlayerAIToolProposalPayload } from "./playerai.types";
import {
  proposalFieldLabel,
  proposalRecord,
  proposalFields,
} from "./proposal-diff";

/** Strip transport metadata recursively; retained values are rendered as text, not HTML. */
export function proposalContent(value: unknown, depth = 0): unknown {
  if (depth > 12) return "Further nested content is not shown.";
  if (Array.isArray(value))
    return value.map((item) => proposalContent(item, depth + 1));
  const record = proposalRecord(value);
  if (!record) return value;
  return Object.fromEntries(
    proposalFields(record).map(([key, item]) => [
      key,
      proposalContent(item, depth + 1),
    ]),
  );
}

export function proposalTitle(payload: PlayerAIToolProposalPayload): string {
  if (
    payload.preview?.kind === "canvas_bundle" ||
    /^canvas\.widget_changes\.apply(?:@\d+)?$/.test(payload.toolId)
  )
    return "Canvas changes";
  const label = payload.preview?.label;
  for (const value of [label, payload.title]) {
    if (
      typeof value === "string" &&
      value.trim() &&
      !/internal product action|@[0-9]+|^[\w-]+\.[\w.-]+$/i.test(value)
    )
      return value;
  }
  return "Proposed changes";
}

export interface ProposalChange {
  label: string;
  before?: unknown;
  after?: unknown;
  comparison: boolean;
}

const PREVIEW_KINDS = new Set([
  "text_replace",
  "widget_replace",
  "widget_create",
  "collection_add",
  "collection_remove",
  "vsm_framing_change_set",
  "vsm_sipoc_change_set",
  "vsm_comment_operation",
  "vsm_kaizen_change_set",
  "vsm_map_change_set",
  "vsm_impact_effort_change_set",
  "vsm_implementation_change_set",
  "vsm_control_plan_change_set",
  "vsm_workshop_notes_change_set",
  "preparation_document_add",
]);

export function proposalChanges(
  payload: PlayerAIToolProposalPayload,
): ProposalChange[] {
  const preview = payload.preview ?? {};
  if (
    preview.kind === "phase_advance" &&
    typeof preview.from_phase === "string" &&
    typeof preview.to_phase === "string"
  ) {
    return [
      {
        label: "Phase",
        before: preview.from_phase,
        after: preview.to_phase,
        comparison: true,
      },
    ];
  }
  if (typeof preview.kind === "string" && !PREVIEW_KINDS.has(preview.kind))
    return [];
  if (Object.hasOwn(preview, "before") && Object.hasOwn(preview, "after")) {
    return [
      {
        label: "Content",
        before: proposalContent(preview.before),
        after: proposalContent(preview.after),
        comparison: true,
      },
    ];
  }
  if (
    preview.kind === "vsm_sipoc_change_set" &&
    Array.isArray(preview.changes)
  ) {
    const changes = preview.changes.flatMap((value) => {
      const change = proposalRecord(value);
      return change && typeof change.label === "string"
        ? [
            {
              label: change.label,
              before: proposalContent(change.before),
              after: proposalContent(change.after),
              comparison: true,
            },
          ]
        : [];
    });
    return [
      ...changes,
      ...(Array.isArray(preview.assumptions) && preview.assumptions.length
        ? fieldChanges({ assumptions: preview.assumptions })
        : []),
    ];
  }
  if (preview.kind === "vsm_framing_change_set") {
    const input = payload.input ?? {};
    const outcomes = Array.isArray(input.outcomes)
      ? input.outcomes.flatMap((value) => {
          const row = proposalRecord(value);
          return row &&
            typeof row.key === "string" &&
            Object.hasOwn(row, "body")
            ? [
                {
                  label: proposalFieldLabel(row.key),
                  after: proposalContent(row.body),
                  comparison: false,
                },
              ]
            : [];
        })
      : [];
    const rest = Object.fromEntries(
      Object.entries(input).filter(([key]) => key !== "outcomes"),
    );
    return [
      ...outcomes,
      ...fieldChanges(proposalRecord(proposalContent(rest)) ?? {}),
      ...(payload.input?.artifact_key === "charter" &&
      typeof preview.calculated_takt_seconds === "number"
        ? [
            {
              label: "Calculated Charter takt",
              after: `${preview.calculated_takt_seconds} s per unit`,
              comparison: false,
            },
          ]
        : []),
    ];
  }
  if (preview.kind === "vsm_workshop_notes_change_set") {
    // PB-5416 — a replaced section is one body of text before and after. The
    // generic rows would print the OLD body inside the "Proposed change" box,
    // which reads as if the old text were the proposal.
    const { replaced_sections: replaced, ...rest } = preview;
    return [
      ...fieldChanges(proposalRecord(proposalContent(rest)) ?? {}),
      ...(Array.isArray(replaced) ? replaced : []).flatMap((value, index) => {
        const row = proposalRecord(value);
        if (!row) return [];
        const heading =
          typeof row.label === "string" && row.label.trim()
            ? row.label
            : `Item ${index + 1}`;
        return [
          {
            label: `${proposalFieldLabel("replaced_sections")} · ${heading}`,
            before: row.before_body,
            after: row.after_body,
            comparison: true,
          },
        ];
      }),
    ];
  }
  return fieldChanges(proposalRecord(proposalContent(preview)) ?? {});
}

function fieldChanges(record: Record<string, unknown>): ProposalChange[] {
  const rows: ProposalChange[] = [];
  for (const [key, value] of Object.entries(record)) {
    if (key === "label" || key === "summary") continue;
    if (
      key.startsWith("after_") &&
      Object.hasOwn(record, key.replace(/^after_/, "before_"))
    )
      continue;
    const afterKey = key.replace(/^before_/, "after_");
    if (key.startsWith("before_") && Object.hasOwn(record, afterKey)) {
      rows.push({
        label: proposalFieldLabel(key.slice(7)),
        before: value,
        after: record[afterKey],
        comparison: true,
      });
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const row = proposalRecord(item);
        const label = [
          row?.label,
          row?.source_label,
          row?.title,
          row?.name,
        ].find((v) => typeof v === "string" && v.trim());
        const prefix = proposalFieldLabel(key);
        if (
          row &&
          Object.hasOwn(row, "before_label") &&
          Object.hasOwn(row, "after_label")
        ) {
          rows.push({
            label: `${prefix} · ${String(row.after_label || row.before_label)}`,
            before: row.before_label,
            after: row.after_label,
            comparison: true,
          });
          const remainder = Object.fromEntries(
            Object.entries(row).filter(
              ([k]) => !["before_label", "after_label"].includes(k),
            ),
          );
          if (Object.keys(remainder).length)
            rows.push({
              label: `${prefix} · Additional changes ${index + 1}`,
              after: remainder,
              comparison: false,
            });
        } else
          rows.push({
            label: `${prefix} · ${label ?? `Item ${index + 1}`}`,
            after: item,
            comparison: false,
          });
      });
    } else
      rows.push({
        label: proposalFieldLabel(key),
        after: value,
        comparison: false,
      });
  }
  return rows;
}
